<template>
  <div class="adventure">
    <!-- Tło z animowanymi elementami -->
    <div class="background-elements">
      <div
        class="fireflies"
        v-for="i in 5"
        :key="i"
        :style="fireflyStyle(i)"
      ></div>
    </div>

    <div class="story-container">
      <div v-if="!adventureCompleted" class="scenario">
        <div class="scenario-text">
          <p>
            {{ currentScenario.text }}
            <span class="emoji">{{ scenarioEmoji }}</span>
          </p>
        </div>

        <div class="choices">
          <button
            v-for="(choice, index) in currentScenario.choices"
            :key="index"
            @click="makeChoice(choice)"
            :class="{
              lose: getCookie('Knowledge') && choice.status === 'lose',
              win: getCookie('Knowledge') && choice.status === 'win',
              disabled: getCookie('Knowledge') && choice.next === 3,
            }"
          >
            {{ choice.text }}
            <span v-if="choice.emoji" class="choice-emoji">{{
              choice.emoji
            }}</span>
          </button>
        </div>
      </div>

      <div v-else class="outcome">
        <p>
          {{ adventureOutcome }} <span class="emoji">{{ outcomeEmoji }}</span>
        </p>

        <button
          v-if="lastStatus === 'lose'"
          class="retry-button"
          @click="restartAdventure"
        >
          Spróbuj ponownie
        </button>

        <button
          v-if="lastStatus === 'win'"
          @click="router.push('/home')"
          class="win-button"
        >
          Powrót do menu
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import router from "@/router";
import { ref, computed } from "vue";

// Funkcja do generowania losowych świetlików
const fireflyStyle = (i) => {
  const top = Math.random() * 80 + 10;
  const left = Math.random() * 90 + 5;
  const animationDuration = Math.random() * 8 + 4;
  return {
    top: `${top}%`,
    left: `${left}%`,
    animationDuration: `${animationDuration}s`,
    animationDelay: `${Math.random() * 3}s`,
  };
};

// Dodane emoji do scenariuszy
const scenarios = [
  {
    index: 0,
    text: "Podróżujesz przez zaklęty las. Dochodzisz do rozwidlenia drogi. Czujesz, że to nie będzie ostatni wybór tego dnia.",
    choices: [
      { text: "Idę w lewo", next: 1 },
      { text: "Idę w prawo", next: 2 },
    ],
  },
  {
    index: 1,
    text: "Na Twojej drodze pojawia się rwąca rzeka. Jak ją pokonasz?",
    choices: [
      {
        text: "Przepływam",
        status: "lose",
        outcome:
          "Mimo tego, że umiesz pływać, rzeka jest za silna i porywa Cię z nurtem. Rozbijasz się o skały przy brzegu.",
      },
      { text: "Skaczę o tyczce", next: 2 },
    ],
  },
  {
    index: 2,
    text: "Widzisz wejście do mrocznej jaskini oraz cichą, tajemniczą polanę...",
    choices: [
      { text: "Wchodzę do jaskini", next: 3 },
      { text: "Idę na polanę", next: 10 },
    ],
  },
  {
    index: 3,
    text: "W jaskini robi się zimno i ciemno. Słyszysz tylko echo własnych kroków...",
    choices: [
      { text: "Wołam: halo?", next: 4 },
      {
        text: "Idę dalej",
        status: "lose",
        outcome:
          "Potykasz się w ciemności i spadasz w rozpadlinę. To długi lot. Zostaje po Tobie tylko czerwona plama.",
      },
    ],
  },
  {
    index: 4,
    text: "Echo odpowiada, ale z opóźnieniem. Na ścianie widzisz dziwny, świecący symbol w kształcie oka.",
    choices: [
      { text: "Dotykam symbolu", next: 5 },
      {
        text: "Niszczę symbol",
        status: "lose",
        outcome:
          "Symbol był pieczęcią. Jaskinia zaczyna się walić, a Ty zostajesz w niej na wieki.",
      },
    ],
  },
  {
    index: 5,
    text: "Symbol znika, a z mroku wyłania się zakapturzona postać. Jej oczy błyszczą w ciemności...",
    choices: [
      { text: "Rozmawiam spokojnie", next: 6 },
      {
        text: "Atakuję!",
        status: "lose",
        outcome:
          "Postać jest szybsza. Unika Twojego ataku i zadaje Ci śmiertelny cios. Ten pojedynek był Twoim końcem.",
      },
    ],
  },
  {
    index: 6,
    text: "Postać ostrzega Cię przed iluzjami i zadaje pytanie: „Czego szukasz?”",
    choices: [
      {
        text: "Skarbu",
        status: "lose",
        outcome:
          "Postać znika. Ta jaskinia pochłania chciwych ludzi. Tułasz się po skalnych korytarzach bez końca.",
      },
      { text: "Drogi wyjścia", next: 7 },
    ],
  },
  {
    index: 7,
    text: "Postać wskazuje tunel prowadzący na zewnątrz. Zdajesz sobie sprawę, że czujesz stamtąd lekki powiew powietrza.",
    choices: [{ text: "Idę tunelem", next: 8 }],
  },
  {
    index: 8,
    text: "Wychodzisz z jaskini bardziej świadomy swojego otoczenia i celu. Znów znajdujesz się w lesie. Nieopodal widzisz krzew z jagodami.",
    choices: [
      { text: "Idę przed siebie", next: 2 },
      {
        text: "Zbieram jagody",
        status: "lose",
        outcome:
          "Mimo ostrzeżeń swojego instynktu, jesz jagody. Niestety, były trujące. Tracisz przytomność i nigdy się nie budzisz.",
      },
    ],
  },
  {
    index: 10,
    text: "Na polanie stoi kamienny ołtarz. Leży na nim świecący, tajemniczy amulet. Czujesz jego moc. Jak to możliwe, że nikt go stąd nie zabrał?",
    choices: [
      { text: "Biorę amulet", next: 11 },
      { text: "Zostawiam go", next: 12 },
    ],
  },
  {
    index: 11,
    text: "Amulet pulsuje energią. Czujesz bijącą od niego moc. Może cię ochronić lub zgubić. Co robisz?",
    choices: [
      {
        text: "Zakładam amulet",
        status: "lose",
        outcome:
          "Amulet był przeklęty. Czujesz jak zalewa cię fala gorąca, tracisz orientację i upadasz. Las zamyka się wokół Ciebie, a Ty tracisz zmysły.",
      },
      { text: "Zostawiam amulet na ołtarzu", next: 12 },
    ],
  },
  {
    index: 12,
    text: "Spotykasz starego wędrowca przy ognisku. Zaprasza Cię do wspólnego posiłku i rozmowy. Mówi, że zna drogę do skarbu ukrytego w lesie. Czy jest godny zaufania?",
    choices: [
      {
        text: "Łączę z nim siły",
        status: "win",
        outcome:
          "Wędrowiec prowadzi Cię bezpiecznie do skarbu. Dzielisz się z nim nagrodą, po czym wracasz do domu bezpieczny i bogatszy. Gratulacje!",
      },
      {
        text: "Poradzę sobie bez niego",
        status: "lose",
        outcome:
          "Wędrowiec wzrusza ramionami, a ty oddalasz się. Niedługo potem gubisz się w labiryncie drzew i nigdy nie znajdujesz drogi do domu. Skarb pozostaje nieodkryty.",
      },
    ],
  },
];

const currentScenarioIndex = ref(0);
const adventureCompleted = ref(false);
const adventureOutcome = ref("");
const lastStatus = ref(null);
const firstTime = ref(true);

// Emoji dla aktualnego scenariusza
const scenarioEmoji = computed(() => {
  return (
    scenarios.find((s) => s.index === currentScenarioIndex.value)?.emoji || ""
  );
});

// Emoji dla wyniku
const outcomeEmoji = computed(() => {
  if (lastStatus.value === "win") return "";
  if (lastStatus.value === "lose") return "";
  return "";
});

const currentScenario = computed(() =>
  scenarios.find((s) => s.index === currentScenarioIndex.value)
);

const setCookie = (name, value, maxAge) => {
  document.cookie = `${name}=${value}; max-age=${maxAge}`;
};

const getCookie = (name) => {
  const cookies = document.cookie.split("; ");
  const cookie = cookies.find((c) => c.startsWith(`${name}=`));
  return cookie ? cookie.split("=")[1] : null;
};

// Opcjonalny dźwięk (można dodać w onMounted)
const playSound = (sound) => {
  if (sound) {
    const audio = new Audio(sound);
    audio.play();
  }
};

const makeChoice = (choice) => {
  if (choice.status) {
    adventureCompleted.value = true;
    adventureOutcome.value = choice.outcome;
    lastStatus.value = choice.status;

    if (choice.status === "win") {
      document.cookie = "puzzle4=completed; max-age=3600";
    }

    if (choice.next === 7) {
      firstTime.value = false;
    }
    return;
  }

  currentScenarioIndex.value = choice.next;

  if (currentScenarioIndex.value === 8) {
    setCookie("Knowledge", "true", 3600);
  }
};

const restartAdventure = () => {
  currentScenarioIndex.value = 0;
  adventureCompleted.value = false;
  adventureOutcome.value = "";
  lastStatus.value = null;
  firstTime.value = true;
};
</script>

<style scoped>
.adventure {
  text-align: center;
  padding: 30px;
  background: linear-gradient(to bottom, #1a2a1a, #2d402d);
  color: #e0e0e0;
  font-family: "Georgia", serif;
  position: relative;
  overflow: hidden;
  min-height: 500px;
  max-width: 600px;
  margin: 0 auto;
  min-height: 100vh;
}

/* Animowane elementy tła */
.background-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}

.tree-left,
.tree-right {
  position: absolute;
  font-size: 40px;
  opacity: 0.7;
  z-index: 0;
}

.tree-left {
  left: 30px;
  top: 50px;
}

.tree-right {
  right: 30px;
  top: 30px;
}

.fireflies {
  position: absolute;
  width: 6px;
  height: 6px;
  background: #fffacd;
  border-radius: 50%;
  box-shadow: 0 0 5px 2px #fffacd;
  opacity: 0.8;
  animation: float linear infinite;
  z-index: 0;
}

@keyframes float {
  0% {
    transform: translateY(0) translateX(0);
    opacity: 0.8;
  }
  50% {
    transform: translateY(-20px) translateX(10px);
    opacity: 0.5;
  }
  100% {
    transform: translateY(0) translateX(0);
    opacity: 0.8;
  }
}

.story-container {
  position: relative;
  z-index: 1;
}

.scenario-text {
  background: rgba(30, 50, 30, 0.7);
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 20px;
  text-align: left;
}

.scenario-text p {
  margin: 0;
  font-size: 1.1em;
  line-height: 1.5;
}

.emoji {
  font-size: 1.2em;
  margin-left: 5px;
}

.choices {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.adventure button {
  display: block;
  margin: 8px auto;
  background-color: #5d4037;
  color: #fffacd;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  width: 80%;
  font-family: "Georgia", serif;
  font-size: 1em;
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
}

.adventure button:hover {
  background-color: #6d4c41;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.adventure button:active {
  transform: translateY(0);
}

.adventure button.lose {
  box-shadow: 0 0 10px #d32f2f;
  background-color: #8b0000;
  color: #ffebee;
}

.adventure button.win {
  box-shadow: 0 0 10px #ffd700;
  background-color: #8b4513;
  color: #fffacd;
}

.adventure button.disabled {
  background-color: #a1887f;
  color: #e0e0e0;
  cursor: not-allowed;
  opacity: 0.6;
  pointer-events: none;
}

.choice-emoji {
  margin-left: 10px;
}

.outcome {
  background: rgba(30, 50, 30, 0.7);
  padding: 25px;
  border-radius: 10px;
  text-align: center;
}

.retry-button {
  background-color: #8b4513;
  margin-top: 20px;
}

.win-button {
  background-color: #2e7d32;
  margin-top: 20px;
}

.retry-button:hover,
.win-button:hover {
  opacity: 0.9;
}
</style>
